import { grey, lightBlue, lime, pink, brown, blueGrey } from '@mui/material/colors';

import Usuzi from './fonts/Usuzi.woff2';
import OldStandardTT from './fonts/oldstandardtt-regular.woff2';

export const brand = {
  50: lime[50],
  200: lime[200],
  500: '#cbdb2a',
  800: lime[800],
};

export const lightblue = {
  50: lightBlue[50],
  200: lightBlue[200],
  500: lightBlue[500],
  800: lightBlue[800],
};

export const highlight = {
  50: pink[50],
  300: pink[300],
  500: pink[500],
  800: pink[800],
};

export const secondary = {
  50: grey[50],
  300: grey[300],
  700: grey[700],
  900: grey[900],
};

const getPalette = (mode) => ({
    mode,
    brand: {
      extralight: mode === 'light' ? lime[100] : brown[900],
      light: brand[200],
      main: brand[500],
      dark: brand[800],
      focus: mode === 'light' ? '#5f6600' : brand[500],
      contrastText: brand[50],
    },
    primary: {
      light: lightblue[200],
      main: lightblue[500],
      dark: lightblue[800],
      contrastText: lightblue[50],
    },
    highlight: {
      light: highlight[300],
      main: highlight[500],
      dark: highlight[800],
      contrastText: highlight[50],
    },
    secondary: {
      light: secondary[300],
      main: secondary[700],
      dark: secondary[900],
      contrastText: secondary[50],
    },
    background: {
      logo: mode === 'light' ? brand[500] : '#fff',
      brand: mode === 'light' ? brand[500] : lime.A100,
      contrast: mode === 'light' ? secondary[900] : '#fff',
      default: mode === 'light' ? '#fff' : '#121212',
      paper: mode === 'light' ? '#fff' : '#1e1e1e',
      services: mode === 'light' ? '#fff' : '#2a2a2a',
      requirements: mode === 'light' ? pink.A200 : '#2a2a2a',
      project: mode === 'light' ? lightBlue.A100 : '#2a2a2a',
      // used for custom button
      greenGradient: mode === 'light'
        ? `linear-gradient(135deg, ${brand[200]} 0%, ${brand[500]} 50%)`
        : 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 50%)',
      // used for animation background
      gradient: mode === 'light'
        ? 'linear-gradient(135deg, #ffffff 10%, #a0a0a0 60%)'
        : 'linear-gradient(135deg, #666666 0%, #1e1e1e 36%)',
      // used for services background
      grayGradient: mode === 'light'
        ? 'linear-gradient(180deg, #ffffff 0%, #eee 50%, #ccc 100%)'
        : 'linear-gradient(180deg, #2a2a2a 0%, #444 100%)',
      // used on homepage for color gradients
      grayGreenGradient: mode === 'light'
        ? `linear-gradient(180deg, #ccc 0%, ${brand[200]} 100%)`
        : `linear-gradient(180deg, #444 0%, ${brown[900]} 100%)`,
      // used on homepage for color gradients
      greenPinkGradient: mode === 'light'
        ? `linear-gradient(180deg, ${brand[200]} 0%, #fec6c7 100%)`
        : `linear-gradient(180deg, ${brown[900]} 0%, ${blueGrey[800]} 100%)`,
      // used on homepage for color gradients
      greenBlueGradient: mode === 'light'
        ? `linear-gradient(180deg, ${brand[200]} 0%, ${lightBlue[100]} 100%)`
        : `linear-gradient(180deg, ${brown[900]} 0%, ${blueGrey[800]} 100%)`,
      // used on homepage for color gradients
      grayBlueGradient: mode === 'light'
        ? `linear-gradient(180deg, #ccc 0%, ${lightBlue[100]} 100%)`
        : `linear-gradient(180deg, #444 0%, #1e1e1e 100%)`,
      // used on homepage for color gradients
      pinkBlueGradient: mode === 'light'
        ? `linear-gradient(180deg, #fec6c7 0%, ${lightBlue[100]} 100%)`
        : `linear-gradient(180deg, ${brown[900]} 0%, ${blueGrey[800]} 100%)`,
      // used on homepage for color gradients
      blueGradient: mode === 'light'
        ? `linear-gradient(180deg, ${lightBlue[100]} 0%, ${lightBlue[400]} 100%)`
        : `linear-gradient(180deg, ${brown[900]} 0%, ${blueGrey[800]} 100%)`,
      // used on homepage for color gradients
      pinkGradient: mode === 'light'
        ? 'linear-gradient(25deg, #f49495 0%, #fec6c7 50%)'
        : `linear-gradient(180deg, ${blueGrey[800]} 0%, ${blueGrey[700]} 50%, #1e1e1e 100%)`,
      // used on homepage for color gradients
      blueGreenGradient: mode === 'light'
        ? `linear-gradient(180deg, ${lightBlue[100]} 0%, ${brand[200]} 100%)`
        : `linear-gradient(180deg, #1e1e1e 0%, ${blueGrey[800]} 50%, ${brown[900]} 100%)`,
      // used for medium page color gradients
      mediumPageGradient: mode === 'light'
        ? `linear-gradient(180deg, #fff 0%, #a0a0a0 50%, ${brand[200]} 100%)`
        : `linear-gradient(180deg, #666666 0%, #1e1e1e 50%, ${brown[900]} 100%)`,
      // used for long page color gradients
      longPageGradient: mode === 'light'
        ? `linear-gradient(180deg, #fff 0%, ${brand[50]} 20%, #fec6c7 70%, ${brand[200]} 100%)`
        : `linear-gradient(180deg, #666666 0%, #1e1e1e 20%, ${blueGrey[800]} 50%, ${brown[900]} 100%)`,
      // used for footer color gradients
      footerGradient: mode === 'light'
        ? `linear-gradient(180deg, ${brand[200]} 0%, ${brand[50]} 100%)`
        : `linear-gradient(180deg, ${brown[900]} 0%, ${blueGrey[800]} 100%)`,
      // used in header and styled lead
      transparent: mode === 'light'
        ? 'rgba(255, 255, 255, 0)'
        : 'rgba(18, 18, 18, 0)',
    },
});

const getDesignTokens = () => ({
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  colorSchemes: {
    light: {
      palette: getPalette('light'),
    },
    dark: {
      palette: getPalette('dark'),
    },
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-checked': {
            color: lightblue[800],
            ...theme.applyStyles('dark', {
              color: lightblue[200],
            }),
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Usuzi';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(${Usuzi}) format('woff2');
        }
        @font-face {
          font-family: 'Old Standard TT';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(${OldStandardTT}) format('woff2');
        }
        .underlined {
          position: relative;
          white-space: nowrap;
        }
        .underlined::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: -0.2em;
          width: 100%;
          border: solid 0.5vw transparent;
          border-bottom-color: ${lime.A100};
          border-radius: 50%;
        }
        #main-content {
          scroll-margin-top: 140px;
        }
        a:focus-visible,
        button:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible,
        [tabindex]:not([tabindex="-1"]):focus-visible {
          outline: 3px solid currentColor;
          outline-offset: 3px;
        }
        @media (forced-colors: active) {
          a:focus-visible,
          button:focus-visible,
          input:focus-visible,
          select:focus-visible,
          textarea:focus-visible,
          [tabindex]:not([tabindex="-1"]):focus-visible {
            outline: 2px solid Highlight;
            outline-offset: 2px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
          }
        }
      `,
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&::before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.54)',
            ...theme.applyStyles('dark', {
              borderBottomColor: 'rgba(255, 255, 255, 0.7)',
            }),
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.54)',
            ...theme.applyStyles('dark', {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            }),
          },
        }),
      },
    },
  },
});

export default getDesignTokens;
